import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxDataGridComponent } from "devextreme-angular";
import CustomStore from "devextreme/data/custom_store";
import notify from "devextreme/ui/notify";
import { forkJoin, lastValueFrom } from "rxjs";
import { FileTransfersService, IOcbFileTransferLog, OcbFileTransferLogTypes, UserService } from "../../core";
import { FileTransferLogComponent } from "../file-transfer-log-popup/file-transfer-log-popup.component";

const now = new Date();
const initialDates: [Date, Date] = [new Date(now), new Date(now)];

@Component({
    selector: 'app-file-transfer-logs',
    templateUrl: './file-transfer-logs.component.html'
})
export class FileTransferLogsComponent implements AfterViewInit {
    @ViewChild('logsGrid', { static: true }) dataGrid?: DxDataGridComponent;
    @ViewChild('logDetailsPopup') logDetailsPopup: FileTransferLogComponent;

    protected dataSource: any;
    protected loadingMessage: string;
    protected maxDate: Date = now;
    protected selectedDates: [Date, Date] = initialDates;
    protected selectedLog: IOcbFileTransferLog = null;

    private fileTransferId: string;
    private fileTransfersLogsTypesLabels: any[];

    constructor(private fileTransfersService: FileTransfersService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private userService: UserService) {
        this.displayFileTransferLogDateAsString = this.displayFileTransferLogDateAsString.bind(this);
        this.displayFileTransferLogType = this.displayFileTransferLogType.bind(this);
    }

    ngAfterViewInit() {
        forkJoin({
            loadingMsg: this.translateService.get('_commons.loading'),
            logTypes: this.translateService.get('fileTransfersLogTypes')
        })
            .subscribe({
                next: (data) => {
                    this.loadingMessage = data.loadingMsg;
                    this.fileTransfersLogsTypesLabels = data.logTypes;
                },
                error: (e) => this.handleError(e)
            });

        this.route.params.subscribe(_ => {
            if (this.route.snapshot.params.ft === 'new') return;
            this.fileTransferId = this.route.snapshot.params.ft;

            this.dataSource = new CustomStore({
                key: 'id',
                load: async () => {
                    const query$ = this.fileTransfersService.getLogs(this.fileTransferId, this.getSelectedStartDate(), this.getSelectedEndDate());
                    const response = await lastValueFrom(query$);
                    return {
                        data: response,
                        totalCount: response.length
                    };
                }
            });
        });
    }


    public displayDate(dateValue): Date {
        const date = new Date(dateValue + 'Z');
        return date;
    }

    private getCurrentLocale(): string {
        let currentLocale = 'en-US';
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; break;
        }
        return currentLocale;
    }

    private getSelectedEndDate() { return this.selectedDates[1].toJSON().slice(0, 10); }

    private getSelectedStartDate() { return this.selectedDates[0].toJSON().slice(0, 10); }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) message = error.error.message;
            else if (error.error.hasOwnProperty('name')) message = error.error.name;
            else message = error.error;
        }
        else message = error;
        notify(message, 'error', 3000);
    }
    

    public displayFileTransferLogDateAsString(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData.transferDate).toLocaleString(locale);
    }

    public displayFileTransferLogType(rowData) {
        return this.fileTransfersLogsTypesLabels[OcbFileTransferLogTypes[rowData.logType]];
    }

    public formatArchivedFileSize(rowData): string {
        if (rowData.archivedFileSize == null) return null;
        return !rowData.archivedFileSize ? '0.00' : rowData.archivedFileSize.toFixed(2);
    }

    public formatSourceFileSize(rowData): string {
        if (rowData.sourceFileSize == null) return null;
        return !rowData.sourceFileSize ? '0.00' : rowData.sourceFileSize.toFixed(2);
    }

    public formatTargetFileSize(rowData): string {
        if (rowData.targetFileSize == null) return null;
        return !rowData.targetFileSize ? '0.00' : rowData.targetFileSize.toFixed(2);
    }

    public onDatesChanged(): void {
        if (!this.dataGrid) return;
        this.dataGrid.instance.refresh();
    }

    public onRefreshButtonClick() {
        if (!this.dataGrid) return;
        this.dataGrid.instance.refresh();
    }

    public onRowDblClick(e) {
        if (!e?.data) return;
        this.selectedLog = e.data;
        this.logDetailsPopup.visible = true;
    }
}